import axios, { AxiosProgressEvent } from 'axios';
import {
  AuthUser,
  DashboardData,
  FeedbackData,
  LogEntry,
  PlotFlag,
  PlotMember,
  PlotMemberList,
  PlotRef,
  PlotSizeGroup,
  PlotSnapshot,
  PlotSnapshotCreated,
  RestoreMarkResponse,
  ServerAction,
  StaffDashboardData,
  StreamChannel,
  TrustedPlayer,
  World,
  WorldAdminSettings,
  WorldAudit,
  WorldBackup,
  WorldCrash,
  WorldSettings,
} from '../types';

/** Shared axios instance. All calls are same-origin under /api. */
export const api = axios.create({ baseURL: '/api' });

/** Extract a human-readable message from an axios/unknown error. */
export function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined;
    if (data && typeof data.error === 'string') return data.error;
    if (error.message) return error.message;
  } else if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

export const Auth = {
  me: () => api.get<AuthUser>('/auth/me').then((r) => r.data),
  logout: () => api.post('/auth/logout'),
  devLogin: () => api.post('/dev/login'),
  discordLoginUrl: '/api/auth/discord',
};

export const Worlds = {
  all: () => api.get<{ worlds: World[] }>('/worlds/all').then((r) => r.data.worlds || []),
  settings: (id: string) =>
    api.get<{ settings: WorldSettings }>(`/worlds/${id}/properties`).then((r) => r.data.settings),
  backups: (id: string) =>
    api.get<{ backups: WorldBackup[] }>(`/worlds/${id}/backups`).then((r) => r.data.backups || []),
  audits: (id: string) =>
    api.get<{ audits: WorldAudit[] }>(`/worlds/${id}/audits`).then((r) => r.data.audits || []),
  logs: (id: string, from?: number | null, before?: number | null) => {
    const params: Record<string, number> = {};
    if (from != null) params.from = from;
    if (before != null) params.before = before;
    return api.get<{ logs: LogEntry[] }>(`/worlds/${id}/logs`, { params }).then((r) => r.data.logs || []);
  },
  /** Admin / developer only. The world's crashed servers and the files copied off each, newest first. */
  crashes: (id: string) => api.get<{ crashes: WorldCrash[] }>(`/worlds/${id}/crashes`).then((r) => r.data.crashes || []),
  /** One copied file of a crash (`WorldCrash.serverId` / `timestamp`), as text. */
  crashFile: (id: string, serverId: string, timestamp: number, name: string) =>
    api
      .get<string>(`/worlds/${id}/crashes/${encodeURIComponent(serverId)}/${timestamp}/${encodeURIComponent(name)}`, {
        responseType: 'text',
      })
      .then((r) => r.data),
  uploadBackup: (id: string, file: File, onProgress: (pct: number) => void) => {
    const form = new FormData();
    form.append('backup', file);
    return api.post<{ backupFileName?: string }>(`/worlds/${id}/backups/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e: AxiosProgressEvent) => {
        if (e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
  },
  /** Take a backup of a running world right now (409 when it is offline or mid-transition). */
  snapshotNow: (id: string) =>
    api.post<{ success: boolean; key: string; backupFileName: string }>(`/worlds/${id}/backups`).then((r) => r.data),
  /**
   * Mark a backup as the one to restore. A running world shuts down and comes back with it right away
   * (same flow as the in-game backups menu); an offline world restores it the next time it starts.
   */
  markRestore: (id: string, fileName: string) =>
    api
      .post<RestoreMarkResponse>(`/worlds/${id}/backups/${encodeURIComponent(fileName)}/restore`)
      .then((r) => r.data),
  /** Remove the pending-restore mark. Only possible while the restore hasn't started. */
  unmarkRestore: (id: string, fileName: string) =>
    api
      .delete<RestoreMarkResponse>(`/worlds/${id}/backups/${encodeURIComponent(fileName)}/restore`)
      .then((r) => r.data),
  serverAction: (id: string, action: ServerAction) => api.post(`/worlds/${id}/server/${action}`),
  /** Only accepted while the world is offline; the new region applies the next time it starts. */
  changeRegion: (id: string, region: string) =>
    api.put<{ success: boolean; region: string; world: World }>(`/worlds/${id}/region`, { region }).then((r) => r.data),
  /** Admin / developer only. Sends just the settings that changed; toggles apply to a running server immediately. */
  updateAdminSettings: (id: string, patch: Partial<WorldAdminSettings>) =>
    api.put<{ success: boolean; world: World }>(`/worlds/${id}/admin`, patch).then((r) => r.data.world),
  /** Developer only. Runs a console command on the running server; 200 means sent, the output shows up in the live log. */
  consoleCommand: (id: string, command: string) =>
    api.post<{ success: boolean; command: string }>(`/worlds/${id}/console`, { command }).then((r) => r.data),
  trusted: (id: string) =>
    api.get<{ trusted: TrustedPlayer[] }>(`/worlds/${id}/trusted`).then((r) => r.data.trusted || []),
  addTrusted: (id: string, username: string) =>
    api.post<TrustedPlayer>(`/worlds/${id}/trusted`, { username }).then((r) => r.data),
  removeTrusted: (id: string, uuid: string) => api.delete(`/worlds/${id}/trusted/${encodeURIComponent(uuid)}`),
  backupDownloadUrl: (id: string, fileName: string) =>
    `/api/worlds/${id}/backups/${encodeURIComponent(fileName)}`,
  mapRenderUrl: (id: string) => `/api/worlds/${id}/map-render`,
};

/** Live world streams. Same origin as the API, so the session cookie travels with the upgrade. */
export const Streams = {
  url: (worldId: string, channel: StreamChannel) => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const q = new URLSearchParams({ worldId, channel });
    return `${proto}://${window.location.host}/api/ws/worlds?${q.toString()}`;
  },
};

const plotPath = (ref: PlotRef) =>
  `/plots/${encodeURIComponent(ref.size)}/${encodeURIComponent(ref.world)}/${encodeURIComponent(ref.plot)}`;

/**
 * The caller's PlotSquared plots. Every call is forwarded by the control service to the plot server for
 * that size (BakeryPlotSquaredSnapshots API) with the caller's Discord id, so only their own plots exist here.
 */
export const Plots = {
  /** Every configured plot size with the caller's plots; a plot server that could not be asked carries an `error` instead. */
  all: () => api.get<{ sizes: PlotSizeGroup[] }>('/plots').then((r) => r.data.sizes || []),
  members: (ref: PlotRef, list: PlotMemberList) => api.get<PlotMember[]>(`${plotPath(ref)}/${list}`).then((r) => r.data || []),
  /** `player` is a Minecraft username, a uuid, or * for everyone. Adding also takes the player off the other list. */
  addMember: (ref: PlotRef, list: PlotMemberList, player: string) =>
    api.put<PlotMember>(`${plotPath(ref)}/${list}/${encodeURIComponent(player)}`).then((r) => r.data),
  removeMember: (ref: PlotRef, list: PlotMemberList, player: string) =>
    api.delete(`${plotPath(ref)}/${list}/${encodeURIComponent(player)}`),
  /** Only the flags the plot sets itself; everything else is the server default. */
  flags: (ref: PlotRef) => api.get<PlotFlag[]>(`${plotPath(ref)}/flags`).then((r) => r.data || []),
  /** The value is parsed by the plot server exactly as "/plot flag set" would (400 with the reason when it isn't valid). */
  setFlag: (ref: PlotRef, name: string, value: string) =>
    api.put<PlotFlag>(`${plotPath(ref)}/flags/${encodeURIComponent(name)}`, { value }).then((r) => r.data),
  removeFlag: (ref: PlotRef, name: string) => api.delete(`${plotPath(ref)}/flags/${encodeURIComponent(name)}`),
  snapshots: (ref: PlotRef) => api.get<PlotSnapshot[]>(`${plotPath(ref)}/snapshots`).then((r) => r.data || []),
  /** Capture the plot now. Takes a while: the plot server saves the world first and waits for the files to settle. */
  snapshotNow: (ref: PlotRef) => api.post<PlotSnapshotCreated>(`${plotPath(ref)}/snapshots`).then((r) => r.data),
  snapshotDownloadUrl: (ref: PlotRef, createdAt: number) => `/api${plotPath(ref)}/snapshots/${createdAt}`,
};

export const Players = {
  usernames: (uuids: string[]) =>
    api.post<Record<string, string>>('/usernames', { uuids }).then((r) => r.data || {}),
  uuidByUsername: (username: string) =>
    api
      .get<{ uuid: string }>(`/player/uuid/${encodeURIComponent(username)}`)
      .then((r) => r.data?.uuid || null),
};

export const Dashboard = {
  user: () => api.get<DashboardData>('/dashboard').then((r) => r.data),
  staff: () => api.get<StaffDashboardData>('/dashboard/staff').then((r) => r.data),
};

export const Feedback = {
  submit: (data: FeedbackData) => api.post('/feedback', data),
};
